import React, { useState, useEffect } from "react";
import Modal from "../modal/Modal";
import "./DynamicTabular.css";

// Helper function to format date
const formatDate = (date, format) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
};

const normalizeDateTime = (dateTime) => {
  // Replace PDT with the timezone offset for Pacific Daylight Time
  const isoString = dateTime.replace("PDT", "-07:00").trim();
  if (isNaN(new Date(isoString))) {
    console.error("Invalid DateTime:", dateTime);
    return null;
  }
  return new Date(isoString).toISOString();
};


// Helper function to format ISO dateTime into PDT format
const formatToPDT = (isoDateTime) => {
  try {
    if (!isoDateTime || isNaN(new Date(isoDateTime))) {
      console.error("Invalid ISO DateTime:", isoDateTime);
      return "Invalid Date";
    }

    const formatter = new Intl.DateTimeFormat("en-US", {
      // timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      // timeZoneName: "short", // Include PDT
    });

    return formatter.format(new Date(isoDateTime));
  } catch (error) {
    console.error("Error formatting ISO dateTime:", error);
    return "Invalid Date";
  }
};



const DynamicTabular = ({
  api,
  headers,
  fields,
  fieldProperties,
  actions,
  navigateToSection,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRowToDelete, setCurrentRowToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [type, setType] = useState("confirm");

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(api);
        if (!response.ok) throw new Error("Failed to fetch data");

        const responseBody = await response.json();
        console.log(responseBody);
        setRows(responseBody?.data?.rows || []);
        console.log(rows);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (api) fetchData();
  }, [api]);

  // Handle Add Action
  const handleAdd = () => {
    const { link } = actions.add || {};
    if (!link) return;

    if (link.startsWith("section-") && navigateToSection) {
      // Internal navigation
      navigateToSection(link);
    } else if (link) {
      // External navigation
      window.location.href = link;
    } else {
      console.error("Invalid link provided for the Add action.");
    }
  };

  // Handle Dynamic Actions (Edit, Delete, etc.)
  const handleAction = async (actionType, id) => {
    const action = actions[actionType];
    if (!action?.api) return;

    const actionUrl = action.api.replace("{id}", id);

    switch (actionType) {
      case "edit":
        window.location.href = actionUrl; // Redirect to the edit page
        break;
      case "delete":
        setCurrentRowToDelete(id);
        setIsModalOpen(true); // Show confirmation modal
        break;
      default:
        console.error(`Unhandled action type: ${actionType}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!currentRowToDelete || !actions.delete?.api) return;

    const deleteUrl = actions.delete.api.replace("{id}", currentRowToDelete);
    try {
      const response = await fetch(deleteUrl, { method: "DELETE" });
      if (!response.ok) throw new Error(actions.delete.errorMessage);

      setRows((prevRows) =>
        prevRows.filter((row) => row.id !== currentRowToDelete)
      );
      setMessage(actions.delete.successMessage);
      setType("success");
    } catch (err) {
      setMessage(actions.delete.errorMessage);
      setType("error");
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsModalOpen(false); // Close modal on cancel
  };

  const handleCloseMessage = () => {
    setMessage("");
    setType("confirm");
  };

  const handleCopyField = (value) => {
    navigator.clipboard.writeText(value).then(() => {
      setMessage("Field value copied to clipboard!");
      setType("success");
      setTimeout(() => setMessage(""), 2000);
    });
  };

  const renderField = (field, value) => {
    const properties = fieldProperties[field];
  
    if (properties?.type === "date" && value) {
      return formatDate(value, properties?.format || "MM/dd/yyyy hh:mm a"); // Fallback formatting
    }
  
    if (properties?.type === "button" && value) {
      return (
        <button
          onClick={() => window.open(value, "_blank")}
          className="dynamic-tabular-button"
        >
          {properties?.buttonLabel || "Action"}
        </button>
      );
    }
  
    if (properties?.type === "label" && value) {
      return <span>{value}</span>;
    }
  
    return value;
  };
  

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="dynamic-tabular">
      {/* Render Table Header */}
      <div className="dynamic-tabular-header">
        {headers.map((header, index) => (
          <div key={index} className="dynamic-tabular-header-cell">
            {header}
          </div>
        ))}
        <div className="dynamic-tabular-header-cell">Actions</div>
      </div>

      {/* Render Table Body */}
      <div className="dynamic-tabular-body">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="dynamic-tabular-row">
            {fields.map((field, fieldIndex) => (
              <div key={fieldIndex} className="dynamic-tabular-cell">
                {fieldProperties[field]?.allowCopy && row[field] && (
                  <div className="copy-icon-container">
                    <i
                      className="fa fa-clipboard copy-icon"
                      onClick={() => handleCopyField(row[field])}
                      title="Copy"
                    ></i>
                  </div>
                )}
                {renderField(field, row[field])}
              </div>
            ))}
            <div className="dynamic-tabular-cell">
              {Object.keys(actions)
                .filter((key) => key !== "add")
                .map((actionKey, actionIndex) => (
                  <button
                    key={actionIndex}
                    onClick={() => handleAction(actionKey, row.id)}
                    className="dynamic-tabular-button"
                  >
                    {actions[actionKey]?.label}
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add Button */}
      <div className="dynamic-tabular-footer">
        <button onClick={handleAdd} className="dynamic-tabular-add-button">
          {actions.add?.label}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        title={actions.delete?.title || "Delete Confirmation"} // Dynamic title
        message={actions.delete?.confirmationDialogMessage || "Are you sure you want to delete this?"}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type="confirm"
      />

      {/* Success/Error Modal */}
      <Modal
        isOpen={!!message}
        message={message}
        onClose={handleCloseMessage}
        type={type}
      />
    </div>
  );
};

export default DynamicTabular;
